// src/components/corkboard/CorkCreatePanel.jsx
//
// The create form that replaces the details panel when one of the "+" buttons
// is pressed: name, description, and an optional first relationship.
//
// The optional relationship is the reason this is a form rather than a button
// that drops a blank card. A story element that is connected to nothing is the
// one state the board cannot show anything useful about, and asking for the
// connection at creation is cheaper than asking the author to come back and add
// it. It stays optional because the first actor on an empty board has nothing
// to connect to yet.
import React, { useMemo, useState } from 'react';
import { Box, Button, MenuItem, TextField, Typography } from '@mui/material';

import { connectTargets } from '../../utils/storyElements';
import { CB, FONT, TYPE_COLOR, TYPE_LABEL, eyebrowSx } from './corkTokens';

const fieldSx = {
  mt: 0.75,
  '& .MuiOutlinedInput-root': {
    fontFamily: FONT.sans,
    fontSize: 12.5,
    backgroundColor: CB.white,
    '& fieldset': { borderColor: CB.lineSoft },
    '&:hover fieldset': { borderColor: CB.purpleSoft },
    '&.Mui-focused fieldset': { borderColor: CB.purple },
  },
};

export default function CorkCreatePanel({ type, board, chapters, onCreate, onCancel }) {
  const label = TYPE_LABEL[type] || type;
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [connectTo, setConnectTo] = useState('');
  const [relLabel, setRelLabel] = useState('');

  const targets = useMemo(
    () => connectTargets({ nodes: board.nodes, chapters }),
    [board.nodes, chapters],
  );

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onCreate?.({
      type,
      name: trimmed,
      desc: desc.trim(),
      connectTo: connectTo || null,
      // A relationship with no label still has to read as a sentence in the
      // editor's Outline view, so it gets a default rather than an empty string.
      relLabel: relLabel.trim() || (type === 'scene' ? 'appears in' : 'connected to'),
    });
  };

  return (
    <Box sx={{ p: 2.75 }}>
      <Typography component="span" sx={{ ...eyebrowSx, color: TYPE_COLOR[type] || CB.goldDeep }}>
        New {label}
      </Typography>
      <Typography sx={{
        fontFamily: FONT.serif, fontSize: 18, fontWeight: 600, mt: 0.5, color: CB.black,
      }}>
        Describe &amp; connect
      </Typography>

      <Typography component="label" sx={{ ...eyebrowSx, display: 'block', mt: 2 }}>
        Name
      </Typography>
      <TextField
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        placeholder={`${label} name`}
        variant="outlined"
        size="small"
        fullWidth
        sx={fieldSx}
      />

      <Typography component="label" sx={{ ...eyebrowSx, display: 'block', mt: 2 }}>
        Description
      </Typography>
      <TextField
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        placeholder={`What is this ${label.toLowerCase()}?`}
        variant="outlined"
        size="small"
        fullWidth
        multiline
        minRows={3}
        sx={fieldSx}
      />

      <Typography component="label" sx={{ ...eyebrowSx, display: 'block', mt: 2 }}>
        Connect to (optional)
      </Typography>
      <TextField
        select
        value={connectTo}
        onChange={(e) => setConnectTo(e.target.value)}
        variant="outlined"
        size="small"
        fullWidth
        sx={fieldSx}
        SelectProps={{ displayEmpty: true }}
      >
        <MenuItem value="">No connection yet</MenuItem>
        {targets.map((t) => (
          <MenuItem key={t.value} value={t.value} sx={{ fontSize: 12.5 }}>
            {t.label}
          </MenuItem>
        ))}
      </TextField>

      {/* The relationship label only means anything once there is a
          relationship, so it appears with one rather than sitting disabled. */}
      {connectTo ? (
        <>
          <Typography component="label" sx={{ ...eyebrowSx, display: 'block', mt: 2 }}>
            Relationship
          </Typography>
          <TextField
            value={relLabel}
            onChange={(e) => setRelLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder="e.g. appears in, rivals, features"
            variant="outlined"
            size="small"
            fullWidth
            sx={fieldSx}
          />
        </>
      ) : null}

      <Box sx={{ display: 'flex', gap: 1, mt: 2.5 }}>
        <Button
          onClick={submit}
          disabled={!name.trim()}
          fullWidth
          sx={{
            fontFamily: FONT.mono, fontSize: 11.5, textTransform: 'none',
            backgroundColor: CB.purple, color: CB.white,
            '&:hover': { backgroundColor: CB.purpleMid },
            '&.Mui-disabled': { backgroundColor: CB.lineSoft, color: CB.gray },
          }}
        >
          Create {label.toLowerCase()}
        </Button>
        <Button
          onClick={onCancel}
          sx={{
            fontFamily: FONT.mono, fontSize: 11.5, textTransform: 'none',
            color: CB.gray, border: `1px solid ${CB.lineSoft}`, flexShrink: 0,
            '&:hover': { borderColor: CB.black, color: CB.black },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}
